import { ChakraTheme, Colors, ThemeConfig, extendTheme } from '@chakra-ui/react';

// Component level customizations
import { accordionTheme } from '@/components/atomic/v1/accordion/Accordion';
import { buttonTheme } from '@/components/atomic/v1/button/Button';
import { calloutTheme } from '@/components/atomic/v1/callout/Callout';
import { cardTheme } from '@/components/atomic/v1/card/cardTheme';
import { checkboxTheme } from '@/components/atomic/v1/checkbox/Checkbox';
import { dataSetTheme } from '@/components/molecular/v1/dataSet/DataSet';
import { dividerTheme } from '@/components/atomic/v1/divider/Divider';
import { formErrorTheme } from '@/components/atomic/v1/form/FormError';
import { formLabelTheme } from '@/components/atomic/v1/form/FormLabel';
import { iconButtonTheme } from '@/components/atomic/v1/iconButton/IconButton';
import { infoCalloutTheme } from '@/components/atomic/v1/infoCallout/InfoCallout';
import { interactiveTagTheme } from '@/components/atomic/v1/interactiveTag/InteractiveTag';
import { menuTheme } from '@/components/atomic/v1/menu/Menu';
import { numberedInputTheme } from '@/components/atomic/v1/numberedInput/NumberedInput';
import { popoverTheme } from '@/components/atomic/v1/popover/Popover';
import { radioTheme } from '@/components/atomic/v1/radio/Radio';
import { selectInputTheme } from '@/components/atomic/v1/select/SelectInput';
import { skeletonTheme } from '@/components/atomic/v1/skeleton/Skeleton';
import { sliderTheme } from '@/components/atomic/v1/slider/Slider';
import { spinnerTheme } from '@/components/atomic/v1/spinner/Spinner';
import { stepperTheme } from '@/components/atomic/v1/stepper/Stepper';
import { tableTheme } from '@/components/atomic/v1/table/Table';
import { tabsTheme } from '@/components/atomic/v1/tabs/Tabs';
import { tagTheme } from '@/components/atomic/v1/tag/Tag';
import { textInputTheme } from '@/components/atomic/v1/textInput/TextInput';
import { modalTheme } from '@/components/atomic/v1/modal/Modal';
import { textareaTheme } from '@/components/atomic/v1/textarea/Textarea';
import { splitButtonTheme } from '@/components/atomic/v1/splitButton/SplitButton';
import { textSwitchTheme } from '@/components/atomic/v1/textSwitch/TextSwitch';

const config: ThemeConfig = {
  cssVarPrefix: 'myomers'
};

/**
 * Breakpoints
 * each entry defines a CSS breakpoint
 * - at 600px: mobile to tablet
 * - at 1024px: tablet to desktop
 * - at 1280: desktop to desktop XL
 */
const breakpoints = {
  base: '0px',
  // We define `sm` at 1px because the effective 'break' point
  // from mobile to tablet happens at 600px [@media(min-width)]
  // Effectively, when we define a `base` style it gets applied
  // as the mobile style, `md` as the tablet style and `lg`
  // as the desktop style.
  sm: '1px',
  md: '600px',
  lg: '1024px',
  xl: '1280px',
  // https://chakra-ui.com/docs/styled-system/responsive-styles#customizing-breakpoints
  // says: If you're using pixels as breakpoint values, make sure to always provide
  //       a value for the 2xl breakpoint, which by its default pixels value is "1536px".
  '2xl': '1536px'
};

const sizes: ChakraTheme['sizes'] = {
  container: {
    '2xl': '1500px'
  }
};

// Color definitions
const colors: Colors = {
  transparent: 'transparent',
  black: '#000000',
  white: '#FFFFFF',
  navy: {
    25: '#CCD8E2',
    50: '#99B0C6',
    75: '#4D759B',
    100: '#003A70'
  },
  orange: {
    25: '#FCDEC9',
    50: '#FABF94',
    75: '#F79E5E',
    100: '#EE7623'
  },
  purple: {
    25: '#D7D9E6',
    50: '#BDC0D5',
    75: '#A3A6CA',
    100: '#7B80AB'
  },
  blue: {
    25: '#E4F1F8',
    50: '#BBDCEF',
    75: '#91C7E5',
    100: '#76B9DE'
  },
  green: {
    25: '#E3EAD3',
    50: '#D1DCB6',
    75: '#B5C689',
    100: '#A2B86C'
  },
  yellow: {
    25: '#FAF0CD',
    50: '#F6E6AC',
    75: '#F2DC8A',
    100: '#EDCD58'
  },
  sky: {
    25: '#F2F8FC',
    75: '#0078B5',
    100: '#005A86'
  },
  grass: {
    25: '#F8FBF6',
    75: '#3B8302',
    100: '#42632C'
  },
  wood: {
    50: '#FFB81C',
    75: '#D5802A',
    100: '#8E4710'
  },
  chili: {
    25: '#FEF2F2',
    75: '#E20101',
    100: '#9A0303'
  },
  cinnamon: {
    25: '#FFFBF4',
    75: '#D36816',
    100: '#B15006'
  },
  grey: {
    10: '#FBFBFB',
    20: '#F6F6F6',
    30: '#EEEEEE',
    40: '#E5E5E5',
    50: '#D1D1D1',
    60: '#B6B6B6',
    70: '#949494',
    80: '#666666',
    90: '#3A3A3A',
    100: '#212529'
  }
};

// Semantic Colors (Tokens)
// https://chakra-ui.com/docs/styled-system/semantic-tokens
const semanticTokens: ChakraTheme['semanticTokens'] = {
  colors: {
    'button-primary/blue:default': 'navy.100',
    'button-primary/blue:hover': 'sky.75',
    'button-primary/blue:active': 'sky.75',
    'button-primary/orange:default': 'cinnamon.75',
    'button-primary/orange:hover': 'cinnamon.100',
    'button-primary/orange:active': 'cinnamon.100',
    'button-primary/disabled': 'grey.60',
    'button-primary/text': 'white',
    'button-secondary/blue:text': 'navy.100',
    'button-secondary/blue:border': 'navy.100',
    'button-secondary/blue:hover': 'sky.75',
    'button-secondary/blue:active': 'sky.75',
    'button-secondary/blue/bg:hover': 'sky.25',
    'button-secondary/blue/bg:active': 'sky.25',
    'button-secondary/orange:text': 'cinnamon.75',
    'button-secondary/orange:border': 'cinnamon.75',
    'button-secondary/orange:hover': 'cinnamon.100',
    'button-secondary/orange:active': 'cinnamon.100',
    'button-secondary/orange/bg:hover': 'cinnamon.25',
    'button-secondary/orange/bg:active': 'cinnamon.25',
    'button-secondary/disabled': 'grey.60',
    'button-tertiary/blue:text': 'navy.100',
    'button-tertiary/blue:hover': 'sky.75',
    'button-tertiary/blue:active': 'sky.75',
    'button-tertiary/blue/bg:hover': 'sky.25',
    'button-tertiary/blue/bg:active': 'sky.25',
    'button-tertiary/orange:text': 'cinnamon.75',
    'button-tertiary/orange:hover': 'cinnamon.100',
    'button-tertiary/orange:active': 'cinnamon.100',
    'button-tertiary/orange/bg:hover': 'cinnamon.25',
    'button-tertiary/orange/bg:active': 'cinnamon.25',
    'button-tertiary/disabled': 'grey.60',
    'icon/default': 'navy.100',
    'icon/hover': 'sky.75',
    'icon/disabled': 'grey.60',
    'icon/white': 'white',
    'status/information/default': 'sky.75',
    'status/information/text': 'sky.100',
    'status/information/bg': 'blue.25',
    'status/information/bg/active': 'blue.50',
    'status/success/default': 'grass.75',
    'status/success/text': 'grass.100',
    'status/success/bg': 'green.25',
    'status/success/bg/active': 'green.50',
    'status/warning/default': 'wood.75',
    'status/warning/text': 'wood.100',
    'status/warning/bg': 'yellow.25',
    'status/warning/bg/active': 'yellow.50',
    'status/error/default': 'chili.75',
    'status/error/text': 'chili.100',
    'status/error/bg': 'chili.25',
    'status/disabled/text': 'grey.80',
    'status/disabled/bg': 'grey.30',
    'text/body': 'grey.90',
    'text/disabled': 'grey.60',
    'text/reverse': 'white',
    'text/secondary': 'grey.70',
    'text/heading': 'navy.100',
    'text/link:default': 'navy.100',
    'text/link:hover': 'sky.75',
    'text/link:active': 'sky.75',
    'text/alert': 'chili.75',
    'text/eyebrow': 'navy.75',
    'background/primary': 'grey.10',
    'background/secondary': 'sky.25',
    'background/white': 'white',
    'background/dark': 'navy.100',
    'background/orange': 'orange.100',
    'border/primary': 'grey.40',
    'border/active': 'sky.75',
    'border/hover': 'sky.75',
    'card/color-strip/blue': 'blue.100',
    'card/color-strip/green': 'green.100',
    'card/color-strip/navy': 'navy.75',
    'card/color-strip/orange': 'orange.75',
    'card/color-strip/purple': 'purple.100',
    'card/color-strip/yellow': 'yellow.100',
    'card/color-strip/red': 'chili.75',
    'form/bg': 'grey.10',
    'form/bg:hover': 'sky.25',
    'form/bg:active': 'sky.25',
    'form/bg:disable': 'grey.30',
    'form/bg:readonly': 'grey.30',
    'form/bg:error': 'chili.25',
    'form/bg:selected': 'sky.75',
    'form/border': 'grey.70',
    'form/border:hover': 'sky.75',
    'form/border:active': 'sky.75',
    'form/border:error': 'chili.75',
    'form/selected:disable': 'blue.100',
    'card-secondary/purple': 'purple.100',
    'icon/purple/default': 'purple.25',
    'icon/purple/active': 'purple.50'
  }
};

// Typography

const typography: Pick<ChakraTheme, 'fonts' | 'fontSizes' | 'fontWeights' | 'lineHeights' | 'letterSpacings'> = {
  fonts: {
    body: 'var(--font-roboto-flex), sans-serif'
  },
  fontSizes: {
    xlarge: '1.5rem',
    large: '1.25rem',
    regular: '1.125rem',
    small: '1rem',
    // default labels in Chakra adjusted to myOMERS values
    sm: '1rem',
    md: '1.125rem',
    lg: '1.25rem',
    xl: '1.5rem'
  },
  fontWeights: {
    light: 300,
    regular: 400,
    medium: 500,
    bold: 700,
    bolder: 900
  },
  lineHeights: {
    normal: 'normal',
    base: 1.5,
    heading: 1.4
  },
  letterSpacings: {
    normal: '0',
    heading: '0.01em',
    allcaps: '0.04em'
  }
};

const textStyles: ChakraTheme['textStyles'] = {
  heading: {
    '2xl': {
      fontWeight: 'bold',
      fontSize: {
        base: '2.5rem',
        md: '3.5rem'
      },
      lineHeight: 1.4,
      letterSpacing: 'heading'
    },
    xl: {
      fontWeight: 'bold',
      fontSize: {
        base: '2rem',
        md: '3rem'
      },
      lineHeight: 1.4,
      letterSpacing: 'heading'
    },
    l: {
      fontWeight: 'bold',
      fontSize: {
        base: '1.75rem',
        md: '2rem'
      },
      lineHeight: 1.5,
      letterSpacing: 'heading'
    },
    m: {
      fontWeight: 'bold',
      fontSize: '1.5rem',
      lineHeight: 1.4,
      letterSpacing: 'heading'
    },
    s: {
      fontWeight: 'bold',
      fontSize: '1.25rem',
      lineHeight: 1.4,
      letterSpacing: 'heading'
    },
    xs: {
      fontWeight: 'bold',
      fontSize: '1.125rem',
      lineHeight: 1.4,
      letterSpacing: 'heading'
    },
    '2xs': {
      fontWeight: 'bold',
      fontSize: '1rem',
      lineHeight: 1.4,
      letterSpacing: 'heading'
    },
    allCapL: {
      fontWeight: 'bolder',
      fontSize: {
        base: '1rem',
        md: '1.5rem'
      },
      lineHeight: 1.5,
      letterSpacing: 'allcaps',
      textTransform: 'uppercase'
    },
    allCapM: {
      fontWeight: 'bolder',
      fontSize: {
        base: '1rem',
        md: '1.25rem'
      },
      lineHeight: 1.5,
      letterSpacing: 'allcaps',
      textTransform: 'uppercase'
    },
    allCapS: {
      fontWeight: 'bolder',
      fontSize: '1rem',
      lineHeight: 1.5,
      letterSpacing: 'allcaps',
      textTransform: 'uppercase'
    }
  },
  body: {
    large: {
      fontSize: 'large',
      lineHeight: 1.5,
      color: 'text/body'
    },
    regular: {
      fontSize: 'regular',
      lineHeight: 1.5,
      color: 'text/body'
    },
    small: {
      fontSize: 'small',
      lineHeight: 1.5,
      color: 'text/body'
    },
    pullQuote: {
      fontSize: 'large',
      lineHeight: 1.5,
      fontStyle: 'italic',
      color: 'text/body'
    }
  },
  button: {
    regular: {
      fontSize: 'large',
      fontWeight: 'bold',
      lineHeight: 1.5,
      letterSpacing: 'heading'
    },
    small: {
      fontSize: 'small',
      fontWeight: 'bold',
      lineHeight: 1.5,
      letterSpacing: 'heading'
    }
  }
};

const shadows: ChakraTheme['shadows'] = {
  'card.light': '2px 2px 12px rgba(77, 117, 155, 0.15)',
  'card.heavy': '10px 10px 30px rgba(77, 117, 155, 0.1)',
  'card.popover': '0px 0px 10px rgba(77, 117, 155, 0.2)'
};

export const theme = extendTheme({
  config,
  breakpoints,
  sizes,
  colors,
  semanticTokens,
  ...typography,
  textStyles,
  shadows,
  components: {
    Tag: tagTheme,
    InteractiveTag: interactiveTagTheme,
    Input: textInputTheme,
    FormLabel: formLabelTheme,
    FormError: formErrorTheme,
    NumberInput: numberedInputTheme,
    Radio: radioTheme,
    Alert: calloutTheme,
    Accordion: accordionTheme,
    Checkbox: checkboxTheme,
    Button: buttonTheme,
    SplitButton: splitButtonTheme,
    Menu: menuTheme,
    Modal: modalTheme,
    Divider: dividerTheme,
    Tabs: tabsTheme,
    Popover: popoverTheme,
    Card: cardTheme,
    Spinner: spinnerTheme,
    Select: selectInputTheme,
    Table: tableTheme,
    Textarea: textareaTheme,
    Stepper: stepperTheme,
    Skeleton: skeletonTheme,
    Slider: sliderTheme,
    IconButton: iconButtonTheme,
    InfoCallout: infoCalloutTheme,
    DataSet: dataSetTheme,
    TextSwitch: textSwitchTheme
  }
});
